// Vuetify styles are imported in app.ts (before app.scss) so CSS layer order is correct.
import {createVuetify, type ThemeDefinition} from 'vuetify';

/**
 * Fleetwire design tokens.
 *
 * Single source of truth for brand colors shared between Vuetify and
 * Tailwind (see tailwind.config.js, which mirrors these values).
 */
export const fleetwireColors = {
    /** Fleet Teal — brand accent from the logo. Background/accent color only: never text on white (2.4:1). */
    primary: '#00BF93',
    /** Accessible Teal — links and text actions on light backgrounds (AA on white). */
    primaryDarken: '#007A61',
    /** Deep Navy — the other logo color; headings and primary dark backgrounds. */
    secondary: '#002339',
    accent: '#00EFB7',
    cta: '#D5A021',
    /** Sidebar: Deep Navy family — brand navy base with lightened hover/active/border steps. */
    sidebar: '#002339',
    sidebarHover: '#0A3A55',
    sidebarActive: '#124867',
    sidebarBorder: '#245C7B',
} as const;

/**
 * Partner / third-party brand colors.
 *
 * These are deliberately constant across light and dark themes — a partner's
 * brand color does not change with our UI mode. They are registered as Vuetify
 * theme colors so partner UI can reference a token (e.g. `color="tesla"` or
 * `rgb(var(--v-theme-getaround))` in SCSS) instead of hardcoding hex inline.
 *
 * Add new partners here rather than sprinkling brand hex throughout components.
 */
export const brandColors = {
    tesla: '#E82127',
    getaround: '#9F3992',
    stripe: '#6D62EC',
} as const;

/**
 * Semantic status colors from the brand guidelines
 * (docs/fleetwire-brand-guidelines_tweaked.html): success/warning/error are
 * the doc's system colors; info and reversal are harmonized extensions
 * (see docs/brand-consistency-audit.md). The guidelines define no dark theme,
 * so the dark set is the same hues lightened for dark surfaces. Component
 * status mapping lives in resources/js/utils/statusColors.ts.
 */
export const statusColors = {
    light: {
        success: '#1E8E5A',
        warning: '#C98A1E',
        error: '#C4433A',
        info: '#33688C',
        reversal: '#6E5A9E',
    },
    dark: {
        success: '#3FAE79',
        warning: '#DFA83F',
        error: '#DB7168',
        info: '#6D9EC0',
        reversal: '#9D8CC7',
    },
} as const;

const light: ThemeDefinition = {
    dark: false,
    colors: {
        primary: fleetwireColors.primary,
        'on-primary': fleetwireColors.secondary,
        'primary-darken-1': fleetwireColors.primaryDarken,
        secondary: fleetwireColors.secondary,
        accent: fleetwireColors.accent,
        cta: fleetwireColors.cta,
        background: '#F7F9FA',
        surface: '#FFFFFF',
        'surface-variant': '#EFF4F7',
        'on-surface-variant': '#000000',
        sidebar: fleetwireColors.sidebar,
        'sidebar-hover': fleetwireColors.sidebarHover,
        'sidebar-active': fleetwireColors.sidebarActive,
        'sidebar-border': fleetwireColors.sidebarBorder,
        error: statusColors.light.error,
        info: statusColors.light.info,
        success: statusColors.light.success,
        warning: statusColors.light.warning,
        reversal: statusColors.light.reversal,
        ...brandColors,
    },
};

const dark: ThemeDefinition = {
    dark: true,
    colors: {
        primary: fleetwireColors.primary,
        'on-primary': fleetwireColors.secondary,
        'primary-darken-1': fleetwireColors.primaryDarken,
        secondary: '#A5BDCB',
        accent: fleetwireColors.accent,
        cta: fleetwireColors.cta,
        background: '#111827',
        surface: '#1F2937',
        'surface-variant': '#374151',
        'on-surface-variant': '#FFFFFF',
        sidebar: '#1F2937',
        'sidebar-hover': fleetwireColors.sidebarHover,
        'sidebar-active': fleetwireColors.sidebarActive,
        'sidebar-border': fleetwireColors.sidebarBorder,
        error: statusColors.dark.error,
        info: statusColors.dark.info,
        success: statusColors.dark.success,
        warning: statusColors.dark.warning,
        reversal: statusColors.dark.reversal,
        ...brandColors,
    },
};

const vuetify = createVuetify({
    theme: {
        defaultTheme: 'light',
        themes: {light, dark},
    },
    display: {
        // Restore Vuetify 3 breakpoint thresholds (v4 lowered md/lg/xl/xxl).
        // Keep in sync with $grid-breakpoints in resources/css/vuetify/settings.scss.
        thresholds: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
            xxl: 2560,
        },
    },
    defaults: {
        // Brand: page-surface cards are flat; overlays keep elevation so they
        // still separate from the scrim/page.
        VCard: {flat: true},
        VDialog: {VCard: {flat: false}},
        VMenu: {VCard: {flat: false}},
        VBottomSheet: {VCard: {flat: false}},
    },
});

export default vuetify;
